/** ONE glass tooltip for the whole app.
 *
 *  Every `title` attribute anywhere in the app is shown as the design's instant
 *  glass pill — the same look as the Read Aloud timeline/slider value tip —
 *  instead of the browser's slow gray box. Nothing has to opt in: the first
 *  hover adopts the element's `title` (moving it to `data-tip`, so the native
 *  popup can never appear), and elements may also declare `data-tip` directly.
 *  Object keys named `title` (dialog configs etc.) are naturally unaffected —
 *  only real DOM attributes ever reach this.
 *
 *  The bubble is ONE body-mounted `position: fixed` element, so it can never be
 *  clipped by an `overflow: hidden` ancestor and always sits above every panel.
 *  It appears above the control (below when the control touches the top edge),
 *  hides on press/scroll/leave, and shows for keyboard focus too. Touch never
 *  shows it — taps act, they don't preview. */
import { el } from '../core/dom';

let tip: HTMLElement | null = null;
let anchor: HTMLElement | null = null;
/** Pending hide (see `hide`) — cancelled when a hint re-appears in time. */
let hideTimer: number | undefined;

function bubble(): HTMLElement {
  if (!tip) {
    tip = el('div', { class: 'app-tip', hidden: true, 'aria-hidden': 'true' });
    document.body.appendChild(tip);
  }
  return tip;
}

function hideNow(): void {
  anchor = null;
  if (hideTimer !== undefined) { clearTimeout(hideTimer); hideTimer = undefined; }
  if (tip) tip.hidden = true;
}

/** Hide after a short grace period.
 *
 *  A hint belongs to ONE element, but the pointer crosses several on its way
 *  across a control's neighbourhood — a verse is `.verse-num` (hinted) beside
 *  `.verse-text` (not), so sweeping over a verse alternates hint → no-hint →
 *  hint many times. Hiding instantly on each of those made the bubble pop in
 *  and out over and over above the verse number (measured: 5 hide/show flips
 *  for 6 crossings), because un-hiding replays the `tip-in` pop. The grace
 *  window lets the pointer pass over unhinted gaps without the bubble
 *  flickering; a real departure still hides it a beat later. */
function hide(): void {
  if (!tip || tip.hidden) { anchor = null; return; }
  if (hideTimer !== undefined) return; // already counting down
  hideTimer = window.setTimeout(() => { hideTimer = undefined; hideNow(); }, 90);
}

/** Centered over the control, above it — below only when it touches the top
 *  edge. A WIDE container (a whole toolbar/header row carrying one hint) is
 *  anchored to the pointer instead: its midpoint is meaningless and usually
 *  lands on some unrelated button in the middle of the row. */
function place(target: HTMLElement, pointerX?: number): void {
  if (!tip) return;
  const r = target.getBoundingClientRect();
  const half = tip.offsetWidth / 2;
  const x = pointerX != null && r.width > 260 ? pointerX : r.left + r.width / 2;
  tip.style.left = `${Math.max(8 + half, Math.min(window.innerWidth - 8 - half, x))}px`;
  const above = r.top - tip.offsetHeight - 7;
  tip.style.top = above >= 6 ? `${above}px` : `${r.bottom + 7}px`;
}

function show(target: HTMLElement, text: string, pointerX?: number): void {
  const t = bubble();
  // A pending hide is cancelled: the pointer came back (or never really left).
  if (hideTimer !== undefined) { clearTimeout(hideTimer); hideTimer = undefined; }
  // Re-entering the SAME anchor must not re-assign `hidden` — that would replay
  // the pop-in animation for a bubble already on screen.
  const sameAnchor = anchor === target && !t.hidden && t.textContent === text;
  anchor = target;
  if (!sameAnchor) {
    t.textContent = text;
    t.hidden = false;
  }
  place(target, pointerX);
}

/** Controls that speak for themselves. A control with no hint of its own must
 *  NOT borrow the hint of a container it sits in: the Read Aloud header says
 *  "Tap to hide Read Aloud", which is true of the header strip but a lie over
 *  the Select Mode and Play buttons inside it (they do their own thing — the
 *  header's click handler already ignores them for exactly this reason). */
const CONTROLS = 'button, select, input, textarea, label, a, [role="button"]';

/** A frame's `title` is its accessible NAME (what a screen reader announces
 *  for the document inside it), never a hover hint. Stripe titles its checkout
 *  frame "Embedded checkout": adopting that moved the name off the frame and
 *  parked a bubble over the payment dialog's Change Amount button for as long
 *  as the pointer stayed in the form (the page never sees it leave). */
const NAMED_NOT_HINTED = 'iframe, frame, embed, object';

/** The hint to show for a hover/focus on `from`, or null when a control
 *  shadows its container's hint. */
function hintTarget(from: Element): HTMLElement | null {
  const titled = from.closest(CONTROLS + ', ' + NAMED_NOT_HINTED + ', [title], [data-tip]') as HTMLElement | null;
  // The nearest of {control, frame, titled thing} wins. When that nearest
  // thing is a control carrying no hint, or a frame, it shadows anything above.
  if (!titled || titled.matches(NAMED_NOT_HINTED)) return null;
  return titled.hasAttribute('title') || titled.hasAttribute('data-tip') ? titled : null;
}

/** The tooltip text an element carries. A live `title` is adopted into
 *  `data-tip` on first sight — code that later re-assigns `title` (state
 *  toggles updating their hint) simply gets re-adopted on the next hover. */
function tipText(target: HTMLElement): string | null {
  const title = target.getAttribute('title');
  if (title) {
    target.setAttribute('data-tip', title);
    target.removeAttribute('title');
  }
  return target.getAttribute('data-tip');
}

/** Wire the app-wide tooltip. Call once at boot. */
export function initTooltips(): void {
  document.addEventListener('pointerover', (e) => {
    if (e.pointerType === 'touch') return;
    const from = e.target as Element | null;
    const target = from ? hintTarget(from) : null;
    if (!target) { hide(); return; }
    const text = tipText(target);
    if (text) show(target, text, e.clientX); else hide();
  });
  document.addEventListener('pointerout', (e) => {
    if (anchor && !anchor.contains(e.relatedTarget as Node | null)) hide();
  });
  // These are unambiguous departures, so they hide AT ONCE (no grace window):
  // a press means the control is being USED — the hint has done its job — and
  // scroll/resize would leave the bubble stranded away from its control.
  document.addEventListener('pointerdown', () => hideNow());
  window.addEventListener('scroll', () => hideNow(), { capture: true, passive: true });
  window.addEventListener('resize', () => hideNow());
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape') hideNow(); });
  // Keyboard reach: focusing a control with a hint shows it, like hover.
  document.addEventListener('focusin', (e) => {
    const from = e.target as Element | null;
    const target = from ? hintTarget(from) : null;
    if (!target || !target.matches(':focus-visible')) return;
    const text = tipText(target);
    if (text) show(target, text);
  });
  document.addEventListener('focusout', () => hideNow());
}
